import React, { useEffect, useState } from "react";
import { FolderKanban, Save, X } from "lucide-react";
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";

import {
    createModuleSectionRequest,
    updateModuleSectionRequest,
} from "../../../redux/actions/moduleCleSectionActions";
import { MODULE_SECTION_STATUS_OPTIONS } from "../moduleCleContentHelpers";

const emptyForm = {
    title: "",
    description: "",
    ordre: "",
    visible: true,
    active: true,
    status: "ACTIF",
};

export default function ModuleCleSectionFormPanel({ open, mode, moduleCleId, section, onClose }) {
    const dispatch = useDispatch();
    const { loading } = useSelector((state) => state.moduleCleSection || {});
    const [form, setForm] = useState(emptyForm);

    useEffect(() => {
        if (!open) return;
        if (mode === "edit" && section) {
            setForm({
                title: section.title || "",
                description: section.description || "",
                ordre: section.ordre ? String(section.ordre) : "",
                visible: section.visible !== false,
                active: section.active !== false,
                status: section.status || "ACTIF",
            });
            return;
        }
        setForm(emptyForm);
    }, [mode, open, section]);

    if (!open) return null;

    const updateField = (field, value) => setForm((previous) => ({ ...previous, [field]: value }));

    const handleSubmit = () => {
        if (!form.title.trim()) {
            Swal.fire({ icon: "warning", text: "Le titre de la section est obligatoire.", timer: 1800, showConfirmButton: false });
            return;
        }
        if (form.ordre && Number(form.ordre) <= 0) {
            Swal.fire({ icon: "warning", text: "L'ordre de la section doit être supérieur à 0.", timer: 1800, showConfirmButton: false });
            return;
        }

        const payload = {
            title: form.title.trim(),
            description: form.description.trim() || null,
            ordre: form.ordre ? Number(form.ordre) : null,
            visible: !!form.visible,
            active: !!form.active,
            status: form.status,
        };

        const callbacks = { onSuccess: () => onClose() };

        if (mode === "edit" && section?.id) {
            dispatch(updateModuleSectionRequest(moduleCleId, section.id, payload, callbacks));
        } else {
            dispatch(createModuleSectionRequest(moduleCleId, payload, callbacks));
        }
    };

    return (
        <aside className={`mcp-panel ${open ? "is-open" : ""}`}>
            <header className="mcp-panel__header">
                <h2>
                    <FolderKanban size={18} />
                    {mode === "edit" ? "Modifier la section" : "Nouvelle section"}
                </h2>
                <button type="button" className="mcp-icon-btn" onClick={onClose}>
                    <X size={20} />
                </button>
            </header>

            <div className="mcp-panel__body">
                <section className="mcp-form-section">
                    <div className="mcp-form-section__title">Informations principales</div>
                    
                    <div className="mcp-field">
                        <label>Titre de la section *</label>
                        <input 
                            value={form.title} 
                            onChange={(event) => updateField("title", event.target.value)} 
                            placeholder="Ex: Introduction au module"
                        />
                    </div>

                    <div className="mcp-field">
                        <label>Description</label>
                        <textarea
                            rows={3}
                            value={form.description}
                            onChange={(event) => updateField("description", event.target.value)}
                            placeholder="Décrivez brièvement le contenu de cette section..."
                        />
                    </div>

                    <div className="mcp-field-group">
                        <div className="mcp-field">
                            <label>Ordre d'affichage</label>
                            <input
                                type="number"
                                min="1"
                                placeholder="Auto"
                                value={form.ordre}
                                onChange={(event) => updateField("ordre", event.target.value)}
                            />
                        </div>
                        <div className="mcp-field">
                            <label>Statut</label>
                            <select value={form.status} onChange={(event) => updateField("status", event.target.value)}>
                                {MODULE_SECTION_STATUS_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="mcp-form-section__title">Visibilité et activation</div>
                    <div className="mcp-field-group">
                        <div className="mcp-field">
                            <label className="mcp-toggle">
                                <input type="checkbox" checked={form.visible} onChange={(event) => updateField("visible", event.target.checked)} />
                                <span>Visible par les apprenants</span>
                            </label>
                        </div>
                        <div className="mcp-field">
                            <label className="mcp-toggle">
                                <input type="checkbox" checked={form.active} onChange={(event) => updateField("active", event.target.checked)} />
                                <span>Section active</span>
                            </label>
                        </div>
                    </div>
                </section>
            </div>

            <footer className="mcp-panel__footer">
                <button type="button" className="mcp-btn mcp-btn--ghost" onClick={onClose}>
                    Annuler
                </button>
                <button type="button" className="mcp-btn mcp-btn--primary" onClick={handleSubmit} disabled={loading}>
                    <Save size={16} />
                    {loading ? "Enregistrement..." : "Enregistrer la section"}
                </button>
            </footer>
        </aside>
    );
}
